import { Box, Typography, IconButton, InputBase } from "@mui/material";
import PropTypes from "prop-types";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { colorsFoundation } from "../../../theme/index.js";

/**
 * SchadenpositionenTable — the click-dummy's Hausrat "Schadenpositionen" editor:
 * an editable table of damaged/stolen items (Gegenstand · Anschaffung · Neuwert €
 * · Schaden €) with per-row delete, an auto-summed total, "Position hinzufügen",
 * and an optional "Betrag übernehmen" that copies the total into the claim amount.
 *
 * Wired as the MDDForm `schadenpositionen` control: `value` is the positions
 * array, `onChange` persists it, `onApplyTotal` writes the sum to another field.
 */
const ACCENT = colorsFoundation.key.blue[600];
const ACCENT_TINT = colorsFoundation.key.blue[50];
const HAIRLINE = colorsFoundation.gray[200];
const MUTED = colorsFoundation.gray[500];

const GRID = "2.6fr 1fr 1fr 1.1fr 42px";
const EMPTY_ROW = { bez: "", jahr: "", neu: "", schaden: "" };

// German money parse/format ("1.234,56" <-> 1234.56).
function parseMoney(v) {
  if (typeof v === "number") return v;
  if (!v) return 0;
  const n = parseFloat(String(v).replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}
const fmtEUR = (n) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(n || 0);

const cellInputSx = {
  width: "100%",
  fontSize: "13px",
  px: "10px",
  py: "7px",
  borderRadius: "6px",
  backgroundColor: colorsFoundation.gray[50],
  border: `1px solid ${HAIRLINE}`,
  "&.Mui-focused": {
    borderColor: ACCENT,
    backgroundColor: colorsFoundation.neutral.white,
  },
};

export default function SchadenpositionenTable({
  value = [],
  onChange,
  onApplyTotal,
  disabled = false,
}) {
  const rows = Array.isArray(value) ? value : [];
  const total = rows.reduce((sum, r) => sum + parseMoney(r.schaden), 0);

  const commit = (next) => !disabled && onChange?.(next);
  const addRow = () => commit([...rows, { ...EMPTY_ROW }]);
  const removeRow = (i) => commit(rows.filter((_, idx) => idx !== i));
  const updateRow = (i, key, v) =>
    commit(rows.map((r, idx) => (idx === i ? { ...r, [key]: v } : r)));

  const HeaderCell = ({ children, align }) => (
    <Typography
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        color: MUTED,
        textAlign: align || "left",
      }}
    >
      {children}
    </Typography>
  );

  return (
    <Box sx={{ width: "100%" }}>
      {/* Heading + running-total pill */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          mb: "12px",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: colorsFoundation.gray[900] }}>
            Beschädigte / entwendete Gegenstände
          </Typography>
          <Typography sx={{ fontSize: "12px", color: MUTED, mt: "2px" }}>
            Erfassen Sie jede Position einzeln – die Schadensumme wird automatisch
            berechnet.
          </Typography>
        </Box>
        <Box
          component="span"
          sx={{
            flex: "none",
            backgroundColor: ACCENT_TINT,
            color: ACCENT,
            borderRadius: "20px",
            padding: "4px 12px",
            fontSize: "12px",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {rows.length} {rows.length === 1 ? "Position" : "Positionen"} ·{" "}
          {fmtEUR(total)}
        </Box>
      </Box>

      {/* Table */}
      <Box
        sx={{
          border: `1px solid ${HAIRLINE}`,
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor: colorsFoundation.neutral.white,
        }}
      >
        {/* Column headers */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: GRID,
            gap: "10px",
            px: "14px",
            py: "10px",
            borderBottom: `1px solid ${HAIRLINE}`,
            backgroundColor: colorsFoundation.gray[50],
          }}
        >
          <HeaderCell>Gegenstand</HeaderCell>
          <HeaderCell>Anschaffung</HeaderCell>
          <HeaderCell align="right">Neuwert (€)</HeaderCell>
          <HeaderCell align="right">Schaden (€)</HeaderCell>
          <span />
        </Box>

        {/* Rows */}
        {rows.length === 0 ? (
          <Typography
            sx={{ fontSize: "13px", color: MUTED, textAlign: "center", py: "24px" }}
          >
            Noch keine Positionen erfasst.
          </Typography>
        ) : (
          rows.map((row, i) => (
            <Box
              key={i}
              sx={{
                display: "grid",
                gridTemplateColumns: GRID,
                gap: "10px",
                alignItems: "center",
                px: "14px",
                py: "8px",
                borderBottom: `1px solid ${colorsFoundation.gray[100]}`,
              }}
            >
              <InputBase
                sx={cellInputSx}
                placeholder="z. B. Notebook"
                value={row.bez || ""}
                disabled={disabled}
                onChange={(e) => updateRow(i, "bez", e.target.value)}
              />
              <InputBase
                sx={cellInputSx}
                placeholder="2022"
                value={row.jahr || ""}
                disabled={disabled}
                onChange={(e) => updateRow(i, "jahr", e.target.value)}
              />
              <InputBase
                sx={{ ...cellInputSx, "& input": { textAlign: "right" } }}
                placeholder="0,00"
                value={row.neu || ""}
                disabled={disabled}
                onChange={(e) => updateRow(i, "neu", e.target.value)}
              />
              <InputBase
                sx={{
                  ...cellInputSx,
                  fontWeight: 600,
                  "& input": { textAlign: "right", fontWeight: 600 },
                }}
                placeholder="0,00"
                value={row.schaden || ""}
                disabled={disabled}
                onChange={(e) => updateRow(i, "schaden", e.target.value)}
              />
              <IconButton
                size="small"
                disabled={disabled}
                aria-label="Position entfernen"
                onClick={() => removeRow(i)}
                sx={{
                  color: MUTED,
                  "&:hover": {
                    backgroundColor: colorsFoundation.key.blue[50],
                    color: ACCENT,
                  },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          ))
        )}

        {/* Footer total */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: "14px",
            py: "10px",
            backgroundColor: colorsFoundation.gray[50],
          }}
        >
          <Typography sx={{ fontSize: "13px", color: MUTED }}>
            Summe der gemeldeten Schäden
          </Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: ACCENT }}>
            {fmtEUR(total)}
          </Typography>
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", gap: "10px", mt: "12px" }}>
        <Box
          component="button"
          type="button"
          onClick={addRow}
          disabled={disabled}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 14px",
            fontSize: "13px",
            fontWeight: 600,
            color: colorsFoundation.gray[700],
            backgroundColor: colorsFoundation.neutral.white,
            border: `1.5px solid ${HAIRLINE}`,
            borderRadius: "8px",
            cursor: disabled ? "default" : "pointer",
            fontFamily: "inherit",
            "&:hover": !disabled && { borderColor: colorsFoundation.gray[400] },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} /> Position hinzufügen
        </Box>
        {onApplyTotal && rows.length > 0 && (
          <Box
            component="button"
            type="button"
            onClick={() => onApplyTotal(total)}
            disabled={disabled}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: 600,
              color: ACCENT,
              backgroundColor: ACCENT_TINT,
              border: "none",
              borderRadius: "8px",
              cursor: disabled ? "default" : "pointer",
              fontFamily: "inherit",
            }}
          >
            Betrag als Schadenhöhe übernehmen
          </Box>
        )}
      </Box>
    </Box>
  );
}

SchadenpositionenTable.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
  onApplyTotal: PropTypes.func,
  disabled: PropTypes.bool,
};
